<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import { defineBreadcrumb, useDevice } from "#imports";

const route = useRoute();
const router = useRouter();
const runtimeConfig = useRuntimeConfig();

const { isMobile } = useDevice();
const currentPage = ref(route.query.page ? parseInt(route.query.page as string) : 1);

const { data, pending } = await useApiFetch('/novels', {
  query: { page: currentPage },
  watch: [currentPage]
});

const onChangePage = (page: number) => {
  router.push({ query: { page: page } });
  if (process.browser){
    nextTick(() => window.scrollTo({ top: 0 }))
  }
};

const crumbs = [
  { label: 'Novels' }
];

const headPaginationLinks = computed(() => {
  let links = [];
  if (currentPage.value > 1) {
    links.push({rel: 'prev', href: runtimeConfig.public.siteURL + route.path + '?page=' + (currentPage.value - 1)})
  }
  if (currentPage.value < data.value?.meta.last_page) {
    links.push({rel: 'next', href: runtimeConfig.public.siteURL + route.path + '?page=' + (currentPage.value + 1)})
  }

  return links;
});

useHead({
  link: headPaginationLinks
});

useSeoMeta({
  titleTemplate: '%s – Read on FictionMe',
  title: `All Books and Novels` + (currentPage.value > 1 ? ` - Page ${currentPage.value}` : ``),
  description: `Discover the best books on FictionMe. Find bestsellers, new releases, and highly-rated books by leading authors`,
  ogTitle: `All Books and Novels`,
  ogDescription: `Discover the best books on FictionMe. Find bestsellers, new releases, and highly-rated books by leading authors`,
  twitterTitle: `All Books and Novels`,
  twitterDescription: `Discover the best books on FictionMe. Find bestsellers, new releases, and highly-rated books by leading authors`,
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      defineListItem({
        position: 1,
        name: 'Novels'
      }),
    ]
  })
]);
</script>

<template>
  <div class="container py-8 max-md:py-6">
    <div class="page-content flex flex-col gap-8">
      <breadcrumbs :breadcrumbs="crumbs" />

      <div class="flex items-start gap-5 max-lg:flex-col">
        <lazy-novels-menu selected="All Novels" />

        <lazy-f-card :title="`Novels & Books`" header-tag="h1">
          <div class="flex flex-col gap-10">
            <p v-if="currentPage === 1">
              <truncated-text
                  text="Welcome to our ‘Novels’ section, a world where each book is a new journey. Lose yourself in the romance of love stories, the grandeur of fantasy realms, or the vibrant pulse of urban tales. Delve into the mysteries of werewolves, feel the thrill of steamy narratives, and experience the growth and challenges in young adult adventures. Our thrilling selections keep you on the edge, while our LGBTQ+ stories celebrate diverse voices and perspectives. Here, every novel is an invitation to a memorable adventure, a path to worlds unexplored. Join us on this literary journey, where every page is a doorway to a new, captivating world."
                  :max="isMobile ? 150 : 500"
                  interactive
              />
            </p>

            <div v-loading="pending" v-if="data" class="grid gap-7 grid-cols-2 max-md:grid-cols-1">
              <book-detailed-card
                  v-for="book in data?.data"
                  :book="book"
                  :key="book.id"
              />
            </div>

            <pagination
                v-if="data?.meta"
                v-model="currentPage"
                :total-pages="data.meta.last_page"
                @changed="onChangePage"
            />
          </div>
        </lazy-f-card>
      </div>

      <cta-banner />
    </div>
  </div>
</template>

<style scoped>

</style>